import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';
import { prefersReducedMotion } from '../utils/dom';

gsap.registerPlugin(SplitText);

const ACTIVE_CLASS = 'is-open';
const MOBILE_MQ = '(max-width: 991px)';
const isTouch = matchMedia('(hover: none) and (pointer: coarse)').matches;

function getOffScreen() {
  return window.matchMedia(MOBILE_MQ).matches
    ? { yPercent: 100, xPercent: 0 }
    : { xPercent: 100, yPercent: 0 };
}

let openPanel = null;
let overlay = null;
let panelLenis = null;
const panelLenisTick = (time) => panelLenis?.raf(time * 1000);

// Tracks per-panel animation state so we can clean up on close / re-open.
const panelAnimations = new WeakMap();

function getOverlay() {
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.className = 'panel-overlay';
  document.body.appendChild(overlay);

  overlay.addEventListener('click', () => closePanel());
  return overlay;
}

function resetPanelInner(panel) {
  const state = panelAnimations.get(panel);
  if (!state) return;

  if (state.observer) state.observer.disconnect();
  state.delayedCalls.forEach((c) => c.kill());
  state.tweens.forEach((t) => t.kill());
  state.splits.forEach((s) => s.revert());

  // Clear any inline styles we applied so next open starts fresh.
  state.clearTargets.forEach((target) => {
    if (!target) return;
    gsap.set(target, { clearProps: 'all' });
  });

  panelAnimations.delete(panel);
}

// Wrap a paragraph's content in an inner span and prime it for the reveal
// tween. The outer element acts as the mask (overflow:hidden).
function prepareParagraphReveal(el, state) {
  let inner = el.querySelector(':scope > .paragraph__inner');
  if (!inner) {
    inner = document.createElement('span');
    inner.className = 'paragraph__inner';
    while (el.firstChild) inner.appendChild(el.firstChild);
    el.appendChild(inner);
  }
  gsap.set(inner, {
    y: parseFloat(getComputedStyle(inner).lineHeight) || 40,
    rotateY: -8,
    opacity: 0,
    transformPerspective: 600,
    willChange: 'transform, opacity',
    transformOrigin: '50% 100%',
  });
  state.clearTargets.push(inner);
  return inner;
}

function animatePanelInner(panel) {
  resetPanelInner(panel);

  // Under reduced motion, skip the staggered content reveal entirely. All the
  // animated targets here prime their hidden state in JS (so leaving them
  // untouched renders them normally) — except subtitle pills, which rest at
  // scale:0 / opacity:0 in CSS and must be forced visible.
  if (prefersReducedMotion()) {
    panel.querySelectorAll('.subtitle').forEach((el) => {
      const bg = el.querySelector('.subtitle__bg');
      const text = el.querySelector('.subtitle__text');
      if (bg) gsap.set(bg, { scale: 1 });
      if (text) gsap.set(text, { opacity: 1 });
    });
    return;
  }

  const state = {
    splits: [],
    clearTargets: [],
    tweens: [],
    delayedCalls: [],
    observer: null,
  };
  panelAnimations.set(panel, state);

  const title = panel.querySelector('.panel__title');
  const subtitle = panel.querySelector('.panel__subtitle');
  const description = panel.querySelector('.panel__description');
  const image = panel.querySelector('.panel__image');
  // Include .rich-text p (e.g. contact-block) so inner blocks also animate.
  const bodyParagraphs = panel.querySelectorAll('.panel__body p, .rich-text p');
  const mediaContainers = panel.querySelectorAll('.media');
  const pillSubtitles = panel.querySelectorAll('.subtitle');
  const listContainers = panel.querySelectorAll('.list');

  // items :: { target, play } — play() starts the reveal tween and records it.
  const items = [];
  const track = (tween) => { state.tweens.push(tween); return tween; };

  if (title) {
    const split = SplitText.create(title, {
      type: 'words', wordsClass: 'word', mask: 'words', aria: false,
    });
    state.splits.push(split);
    gsap.set(split.words, {
      yPercent: 120, rotateY: -20, willChange: 'transform', transformOrigin: '50% 100%',
    });
    items.push({
      target: title,
      play: () => track(gsap.to(split.words, {
        yPercent: 0, rotateY: 0, duration: 1.2, stagger: 0.08, ease: 'power3.out', force3D: true,
        onComplete: () => gsap.set(split.words, { willChange: 'auto' }),
      })),
    });
  }

  if (subtitle) {
    const split = SplitText.create(subtitle, {
      type: 'lines', linesClass: 'line', mask: 'lines', aria: false,
    });
    state.splits.push(split);
    gsap.set(split.lines, { yPercent: 100, willChange: 'transform' });
    items.push({
      target: subtitle,
      play: () => track(gsap.to(split.lines, {
        yPercent: 0, duration: 1, ease: 'power3.out', force3D: true,
        onComplete: () => gsap.set(split.lines, { willChange: 'auto' }),
      })),
    });
  }

  if (description) {
    const inner = prepareParagraphReveal(description, state);
    items.push({
      target: description,
      play: () => track(gsap.to(inner, {
        y: 0, rotateY: 0, opacity: 1, duration: 1.8, ease: 'power3.out', force3D: true,
        onComplete: () => gsap.set(inner, { willChange: 'auto' }),
      })),
    });
  }

  if (image) {
    gsap.set(image, { opacity: 0, scale: 0.92, yPercent: 4 });
    state.clearTargets.push(image);
    items.push({
      target: image,
      play: () => track(gsap.to(image, {
        opacity: 1, scale: 1, yPercent: 0, duration: 1.2, ease: 'power3.out',
      })),
    });
  }

  bodyParagraphs.forEach((p) => {
    const inner = prepareParagraphReveal(p, state);
    items.push({
      target: p,
      play: () => track(gsap.to(inner, {
        y: 0, rotateY: 0, opacity: 1, duration: 1.8, ease: 'power3.out', force3D: true,
        onComplete: () => gsap.set(inner, { willChange: 'auto' }),
      })),
    });
  });

  mediaContainers.forEach((container) => {
    const mediaItems = container.querySelectorAll('.media__item');
    if (!mediaItems.length) return;
    gsap.set(mediaItems, { opacity: 0, yPercent: 30 });
    state.clearTargets.push(mediaItems);
    items.push({
      target: container,
      play: () => track(gsap.to(mediaItems, {
        opacity: 1, yPercent: 0, duration: 1, stagger: 0.06, ease: 'power3.out',
      })),
    });
  });

  pillSubtitles.forEach((el) => {
    const bg = el.querySelector('.subtitle__bg');
    const text = el.querySelector('.subtitle__text');
    if (!bg || !text) return;
    gsap.set(bg, { scale: 0 });
    gsap.set(text, { opacity: 0 });
    state.clearTargets.push(bg, text);
    items.push({
      target: el,
      play: () => {
        track(gsap.to(bg, { scale: 1, duration: 0.6, ease: 'power3.out' }));
        track(gsap.to(text, { opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.2 }));
      },
    });
  });

  listContainers.forEach((container) => {
    // Match homepage's `.list > a` stagger; panels use `<li>` children.
    const children = container.querySelectorAll(':scope > li, :scope > a');
    if (!children.length) return;
    gsap.set(children, { yPercent: 100, opacity: 0 });
    state.clearTargets.push(children);
    items.push({
      target: container,
      play: () => track(gsap.to(children, {
        yPercent: 0, opacity: 1, duration: 1.2, stagger: 0.1, ease: 'power3.out',
      })),
    });
  });

  const targetToItem = new Map(items.map((it) => [it.target, it]));

  // Use the viewport as root. Lenis translates content via transform, and
  // the panel itself slides in via transform — IO tracks both reliably.
  const observer = new IntersectionObserver((entries) => {
    // Batch-entering items cascade slightly so they feel staggered.
    const intersecting = entries.filter((e) => e.isIntersecting);
    intersecting.forEach((entry, i) => {
      const item = targetToItem.get(entry.target);
      if (!item) return;
      const call = gsap.delayedCall(i * 0.08, item.play);
      state.delayedCalls.push(call);
      observer.unobserve(entry.target);
    });
  }, {
    rootMargin: '0px 0px -10% 0px',
  });
  state.observer = observer;

  // Defer observation until after the panel has begun sliding in, so IO
  // measures against the in-position layout rather than the off-screen one.
  const startCall = gsap.delayedCall(0.3, () => {
    items.forEach((it) => observer.observe(it.target));
  });
  state.delayedCalls.push(startCall);
}

// The listing URL the close button / popstate returns to for litigation panels.
const LITIGATION_LISTING = '/litigation-surveys';

// Panel ids that correspond to a real route (have a dedicated URL). Used to
// distinguish "SEO-backed" panels (AJAX-navigable, URL-bearing) from the
// instant-open panels on about/team pages.
const LITIGATION_PANEL_IDS = new Set([
  'panel-confusion',
  'panel-secondary-meaning',
  'panel-genericness',
  'panel-false-advertising',
  'panel-fame-dilution',
  'panel-class-certification',
  'panel-patent',
]);

function wrapPanelScroll(panel) {
  if (panel.querySelector(':scope > .panel__scroll')) return;
  const closeButtons = Array.from(panel.querySelectorAll('.panel__close'));
  closeButtons.forEach((btn) => btn.remove());

  const scroll = document.createElement('div');
  scroll.className = 'panel__scroll';
  const inner = document.createElement('div');
  inner.className = 'panel__scroll__inner';
  while (panel.firstChild) inner.appendChild(panel.firstChild);
  scroll.appendChild(inner);

  closeButtons.forEach((btn) => panel.appendChild(btn));
  panel.appendChild(scroll);
}

function bindPanelCloseButtons(panel) {
  panel.querySelectorAll('.panel__close').forEach((btn) => {
    btn.addEventListener('click', () => closePanel());
  });
}

// Initialize a single panel element: off-screen, scroll-wrapped, close-bound.
// Safe to call on freshly-injected panels after AJAX.
function initPanelElement(panel) {
  gsap.set(panel, getOffScreen());
  wrapPanelScroll(panel);
  bindPanelCloseButtons(panel);
}

export function openPanelById(id, { pushUrl = null, pushState: doPush = true } = {}) {
  const panel = document.getElementById(id);
  if (!panel || openPanel === panel) return;

  if (openPanel) closePanel({ animate: false, resetUrl: false });

  openPanel = panel;
  const ol = getOverlay();

  if (doPush && pushUrl) {
    history.pushState({ panel: id }, '', pushUrl);
  } else if (!doPush && pushUrl) {
    // Auto-open on direct visit: mark current entry with state so back/forward work.
    history.replaceState({ panel: id }, '', pushUrl);
  }

  window.lenis?.stop();
  document.documentElement.classList.add('panel-open');

  const scrollEl = panel.querySelector('.panel__scroll');
  if (scrollEl && !isTouch && !prefersReducedMotion()) {
    panelLenis = new Lenis({
      wrapper: scrollEl,
      smoothWheel: true,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    gsap.ticker.add(panelLenisTick);
  }

  gsap.to(ol, {
    opacity: 1,
    duration: 0.4,
    ease: 'power2.out',
    onStart: () => { ol.style.pointerEvents = 'auto'; },
  });

  panel.classList.add(ACTIVE_CLASS);
  const off = getOffScreen();
  gsap.set(panel, { visibility: 'visible', ...off });
  gsap.to(panel, {
    xPercent: 0,
    yPercent: 0,
    duration: 1,
    ease: 'power3.out',
  });

  animatePanelInner(panel);
}

export function closePanel({ animate = true, resetUrl = true } = {}) {
  if (!openPanel) return;

  const panel = openPanel;
  const ol = getOverlay();
  const wasLitigation = LITIGATION_PANEL_IDS.has(panel.id);
  openPanel = null;

  ol.style.pointerEvents = 'none';
  document.documentElement.classList.remove('panel-open');
  window.lenis?.start();

  if (resetUrl) {
    // Litigation panels map to dedicated URLs — drop back to the listing.
    // Other panels (team/about) just drop their hash and stay on current path.
    const target = wasLitigation
      ? LITIGATION_LISTING
      : window.location.pathname + window.location.search;
    history.replaceState(null, '', target);
  }

  const off = getOffScreen();

  const done = () => {
    panel.classList.remove(ACTIVE_CLASS);
    if (panelLenis) {
      gsap.ticker.remove(panelLenisTick);
      panelLenis.destroy();
      panelLenis = null;
    }
    gsap.set(panel, { visibility: 'hidden', ...off });
    resetPanelInner(panel);
  };

  if (!animate) {
    gsap.set(ol, { opacity: 0 });
    done();
    return;
  }

  gsap.to(panel, {
    ...off,
    duration: 1,
    ease: 'power3.out',
    onComplete: done,
  });

  gsap.to(ol, {
    opacity: 0,
    duration: 0.4,
    ease: 'power2.inOut',
  });
}

// Fetch a topic URL, extract its <aside class="panel">, inject into the current
// document. Returns true on success.
async function fetchAndInjectPanel(url, panelId) {
  if (document.getElementById(panelId)) return true;

  let response;
  try {
    response = await fetch(url, { credentials: 'same-origin' });
  } catch (e) {
    return false;
  }
  if (!response.ok) return false;

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const panelEl = doc.getElementById(panelId);
  if (!panelEl) return false;

  document.body.appendChild(panelEl);
  initPanelElement(panelEl);

  const incomingTitle = doc.querySelector('title')?.textContent;
  if (incomingTitle) document.title = incomingTitle;

  return true;
}

async function navigateToPanel(url, panelId, trigger = null) {
  // Reuse the page-transition cursor loader on desktop; trigger gets
  // `.is-loading` so mobile (where the cursor loader is hidden) shows a
  // spinner on the tapped card.
  document.documentElement.classList.add('is-loading');
  trigger?.classList.add('is-loading');

  const ok = await fetchAndInjectPanel(url, panelId);

  document.documentElement.classList.remove('is-loading');
  trigger?.classList.remove('is-loading');

  if (!ok) {
    // Fetch failed — fall back to a full navigation so the user still gets there.
    window.location.href = url;
    return;
  }
  openPanelById(panelId, { pushUrl: url, pushState: true });
}

function isPlainLeftClick(e) {
  return e.button === 0
    && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export default function initPanel() {
  // Initialize every panel already in the DOM.
  document.querySelectorAll('.panel').forEach(initPanelElement);

  // Triggers: anchors with data-panel AJAX-fetch; non-anchors open instantly.
  document.querySelectorAll('[data-panel]').forEach((trigger) => {
    const id = trigger.dataset.panel;
    if (trigger.tagName === 'A' && trigger.getAttribute('href')) {
      trigger.addEventListener('click', (e) => {
        if (!isPlainLeftClick(e)) return;
        e.preventDefault();
        navigateToPanel(trigger.getAttribute('href'), id, trigger);
      });
    } else {
      trigger.addEventListener('click', () => openPanelById(id));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePanel();
  });

  window.addEventListener('popstate', async (e) => {
    const id = e.state?.panel;
    if (id) {
      if (!document.getElementById(id)) {
        await fetchAndInjectPanel(window.location.pathname, id);
      }
      if (document.getElementById(id)) {
        openPanelById(id, { pushState: false });
      }
    } else {
      closePanel({ resetUrl: false });
    }
  });

  // Auto-open on direct visit to a topic URL (body[data-auto-panel="panel-id"]).
  const autoPanelId = document.body.dataset.autoPanel;
  if (autoPanelId && document.getElementById(autoPanelId)) {
    const ready = document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();
    ready.then(() => requestAnimationFrame(() => {
      openPanelById(autoPanelId, {
        pushUrl: window.location.pathname + window.location.search,
        pushState: false,
      });
    }));
    return;
  }

  // Legacy hash support (e.g. /team#panel-rodman).
  const hash = window.location.hash.slice(1);
  if (hash && document.getElementById(hash)) {
    const ready = document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();
    ready.then(() => requestAnimationFrame(() => openPanelById(hash)));
  }
}
